import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, CalendarDays, MapPin, Ticket, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import VisitorNavBar from "../../components/Common/VisitorNavBar";
import { useAuth } from "../../contexts/AuthContext";
import { listMyBookings } from "../../lib/mudoNetwork/bookingsApi";

function fmtWhen(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function getBookingTitle(b) {
  if (b?.workshop?.title) return b.workshop.title;
  if (b?.space_slot?.space?.title) return b.space_slot.space.title;
  if (b?.space_slot?.space?.name) return b.space_slot.space.name;
  return b?.title || b?.type || "Booking";
}

function getBookingSubtitle(b) {
  const studioName = b?.studio?.display_name || b?.studio?.name;
  const locationParts = [b?.studio?.city, b?.studio?.country].filter(Boolean);
  if (!studioName && !locationParts.length) return null;
  return {
    studioName: studioName || null,
    locationText: locationParts.join(", ") || null,
  };
}

export default function MyBookingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const isDev = import.meta?.env?.MODE === "development";

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;

    async function load() {
      if (!user?.id) {
        setLoading(false);
        setError("You need to be logged in to view your bookings.");
        return;
      }

      setLoading(true);
      setError("");

      try {
        const { data, error: e } = await listMyBookings(user.id, { limit: 100 });
        if (e) throw e;
        if (!alive) return;
        setBookings(Array.isArray(data) ? data : []);
      } catch (err) {
        if (isDev) console.warn("[MyBookings] load error", err);
        if (!alive) return;
        setError(err?.message || "Failed to load bookings.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
    };
  }, [user?.id]);

  const sortedBookings = useMemo(() => {
    const copy = Array.isArray(bookings) ? [...bookings] : [];
    copy.sort((a, b) => {
      const ams = new Date(a?.updated_at || a?.created_at || 0).getTime();
      const bms = new Date(b?.updated_at || b?.created_at || 0).getTime();
      return bms - ams;
    });
    return copy;
  }, [bookings]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white">
      <VisitorNavBar showBackButton backTo="/discover" />

      <div className="pt-24 pb-12 px-4 max-w-5xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-sm text-white/70"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-2xl font-bold mb-1">My Bookings</h1>
        <p className="text-sm text-white/60 mb-6">
          Your workshops and studio space bookings.
        </p>

        {loading && (
          <div className="flex items-center justify-center py-16">
            <div className="inline-block w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="mt-4 p-4 rounded-xl border border-red-500/30 bg-red-500/10 text-red-200 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div>
              <div className="font-semibold">Couldn’t load bookings</div>
              <div className="text-sm opacity-90">{error}</div>
            </div>
          </div>
        )}

        {!loading && !error && sortedBookings.length === 0 && (
          <div className="text-center py-16 text-white/70">
            <Ticket className="w-12 h-12 mx-auto mb-4 text-white/30" />
            <p className="text-lg mb-1">No bookings yet</p>
            <p className="text-sm text-white/50">
              Book a workshop or reserve studio time to see it here.
            </p>
          </div>
        )}

        {!loading && !error && sortedBookings.length > 0 && (
          <div className="space-y-3 mt-4">
            {sortedBookings.map((b) => (
              <BookingRow key={b.id || `${b.created_at}-${b.type}`} booking={b} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BookingRow({ booking }) {
  const title = getBookingTitle(booking);
  const subtitle = getBookingSubtitle(booking);

  const when = fmtWhen(booking?.start_at || booking?.workshop?.start_at || booking?.space_slot?.start_at || booking?.created_at);
  const status = String(booking?.status || "pending");

  const statusClass =
    status === "confirmed" || status === "paid"
      ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
      : status === "cancelled" || status === "canceled"
      ? "text-red-300 bg-red-500/10 border-red-500/30"
      : "text-amber-300 bg-amber-500/10 border-amber-500/30";

  return (
    <div className="flex gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
      <div className="w-12 h-12 rounded-lg bg-black/30 flex items-center justify-center flex-shrink-0">
        <CalendarDays className="w-6 h-6 text-white/40" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{title}</p>
            {subtitle && (
              <p className="text-xs text-white/60 truncate mt-0.5 flex items-center gap-1">
                {subtitle.studioName ? (
                  <span className="font-medium text-amber-200">{subtitle.studioName}</span>
                ) : null}
                {subtitle.locationText ? (
                  <>
                    <span className="text-white/40">•</span>
                    <MapPin className="w-3 h-3" />
                    <span>{subtitle.locationText}</span>
                  </>
                ) : null}
              </p>
            )}
          </div>

          <span className={`px-2 py-0.5 rounded-full text-[11px] border ${statusClass}`}>
            {status}
          </span>
        </div>

        {when ? (
          <div className="mt-2 text-xs text-white/60">{when}</div>
        ) : null}
      </div>
    </div>
  );
}
